package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.ApiErrorView
import co.maytes.standupbot.dashboard.components.motion.CountUp
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.DisplayHeadline
import co.maytes.standupbot.dashboard.components.widgets.DisplaySize
import co.maytes.standupbot.dashboard.components.widgets.Eyebrow
import co.maytes.standupbot.dashboard.features.setup.SetupGlyph
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Spacing
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.dom.Text

private const val SKELETON_RUN_ROWS = 4
private const val SKELETON_DAYS = 7

@Composable
fun HomeScreen() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val setupStore = rememberSetupStatusStore()

    val user = auth.currentUser
    val workspaceId = user?.workspace?.id
    val firstName = (user?.displayName ?: "there").substringBefore(' ').ifBlank { "there" }
    val workspaceTimezone = user?.workspace?.defaultTimezone ?: "UTC"
    val canManage = auth.isAdmin
    val setupVisible = canManage && !auth.requiresOnboarding()

    val container = remember(api) { HomeContainer(scope, api) }
    val state by container.subscribe { action ->
        when (action) {
            is HomeContract.Action.Navigate -> ctx.router.navigateTo(action.path)
        }
    }
    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) container.intent(HomeContract.Intent.Load(workspaceId, canManage))
    }

    val setupState = setupStore.state
    val setupStatus = if (setupVisible) setupState.status else null
    val journey = remember(setupStatus) { setupStatus?.let(SetupJourney::from) }
    val setupPending = setupVisible && setupStatus == null && setupState.error == null
    val flow = HomeFlow.from(setupStatus, canAct = canManage)

    val now = Clock.System.now()
    val tz = remember { TimeZone.currentSystemDefault() }
    val ready = state.status == HomeContract.Status.Ready
    val strip = remember(setupState.status, state.runs, canManage, ready) {
        if (auth.requiresOnboarding()) {
            HealthStripModel.hidden()
        } else {
            HealthStripModel.from(setupState.status, if (ready) state.runs else emptyList(), canManage, Clock.System.now(), tz)
        }
    }
    val line = when {
        state.status is HomeContract.Status.Error -> HeroLine("We couldn't load your workspace.", null)
        setupPending || (!ready && (journey == null || journey.complete)) -> null
        else -> HomeModel.hero(canManage, journey, if (ready) state.standups else null, now, tz)
    }

    HmDiv(HomeClasses.ROOT) {
        HomeHero(
            greeting = "${HomeModel.greetingFor(now.toLocalDateTime(tz).hour)}, $firstName.",
            dateLabel = HomeModel.longDate(now, tz),
            line = line,
            stats = if (ready) HeroStats.from(state, now, tz) else null,
            flow = flow,
        )
        HomeHealthStrip(strip, baseIndex = 1, showCheckReport = journey?.complete != false)
        SetupSection(journey = journey, pending = setupPending, stripShown = strip.mode != StripMode.Hidden)
        StateSwap(state = phaseKey(state.status), rise = false) {
            when (val status = state.status) {
                HomeContract.Status.Loading -> HomeSkeleton()
                is HomeContract.Status.Error -> ApiErrorView(
                    error = status.error,
                    resourceLabel = "dashboard",
                    onRetry = {
                        if (!workspaceId.isNullOrBlank()) container.intent(HomeContract.Intent.Load(workspaceId, canManage))
                    },
                )
                HomeContract.Status.Ready -> ReadyContent(state, workspaceId, workspaceTimezone, canManage, now, tz)
            }
        }
    }
}

private fun phaseKey(status: HomeContract.Status): Int =
    when (status) {
        HomeContract.Status.Loading -> 0
        HomeContract.Status.Ready -> 1
        is HomeContract.Status.Error -> 2
    }

@Composable
private fun SetupSection(journey: SetupJourney?, pending: Boolean, stripShown: Boolean) {
    when {
        pending -> StateSwap(state = SETUP_PENDING, rise = false) { SetupSkeleton() }
        journey == null -> Unit
        else -> {
            val compact = journey.complete || (journey.pendingCount <= 1 && journey.next?.glyph != SetupGlyph.Broken)
            val baseIndex = if (stripShown) 2 else 1
            StateSwap(state = compact) {
                when {
                    journey.complete -> if (!stripShown) HomeSetupStrip(baseIndex = 1)
                    compact -> HomeSetupStrip(baseIndex = baseIndex, remaining = journey.next, remainingCount = journey.pendingCount)
                    else -> HomeSetupView(journey, baseIndex = baseIndex)
                }
            }
        }
    }
}

private const val SETUP_PENDING = "setup-pending"

@Composable
private fun ReadyContent(
    state: HomeContract.State,
    workspaceId: String?,
    workspaceTimezone: String,
    canManage: Boolean,
    now: Instant,
    tz: TimeZone,
) {
    HmDiv(HomeClasses.ROOT) {
        HomeLiveView(
            HomeLiveData(
                now = now,
                tz = tz,
                workspaceId = workspaceId,
                workspaceTimezone = workspaceTimezone,
                runs = state.runs,
                standups = state.standups,
                members = state.members,
                canManage = canManage,
                unlinked = state.unlinked,
            ),
        )
        state.billingStatus?.let { PlanBanner(status = it) }
    }
}

data class HeroStats(val postedThisMonth: Int, val activeStandups: Int, val spendLabel: String) {
    companion object {
        fun from(state: HomeContract.State, now: Instant, tz: TimeZone): HeroStats {
            val month = HomeModel.postedThisMonth(state.runs, now, tz)
            return HeroStats(
                postedThisMonth = month.size,
                activeStandups = state.standups.count { it.enabled },
                spendLabel = HomeModel.formatDollars(month.sumOf { it.aiCostCents }),
            )
        }
    }
}

@Composable
private fun HomeHero(greeting: String, dateLabel: String, line: HeroLine?, stats: HeroStats?, flow: HomeFlow) {
    val nav = rememberNavigator()
    HmDiv(HomeClasses.PANEL, HomeClasses.HERO, modifier = Modifier.staggerIn(0)) {
        HmDiv(HomeClasses.HERO_COPY) {
            Eyebrow(text = dateLabel, withDot = true)
            DisplayHeadline(text = greeting, size = DisplaySize.Sm)
            StateSwap(state = line?.text.orEmpty()) {
                if (line == null) {
                    HmDiv(modifier = Modifier.stack(Spacing.xs)) {
                        HmBone("85%", "1rem")
                        HmBone("55%", "1rem")
                    }
                } else {
                    HmDiv(modifier = Modifier.stack(Spacing.sm)) {
                        HmLead(line.text)
                        line.action?.let { action ->
                            HmDiv(modifier = Modifier.rowWrap()) {
                                Button(
                                    label = action.label,
                                    variant = ButtonVariant.Secondary,
                                    size = ButtonSize.Sm,
                                    onClick = { nav(action.path) },
                                )
                            }
                        }
                    }
                }
            }
            StateSwap(state = stats == null) {
                HmDiv(HomeClasses.STATS) {
                    StatTile("Posted this month", stats?.postedThisMonth)
                    StatTile("Active standups", stats?.activeStandups)
                    StatTile("AI spend", stats?.spendLabel)
                }
            }
        }
        HmDiv(HomeClasses.ART) { HomeFlowView(flow) }
    }
}

@Composable
private fun StatTile(label: String, value: Any?) {
    HmDiv(HomeClasses.STAT) {
        HmDiv(HomeClasses.STAT_VALUE) {
            when (value) {
                null -> HmBone("3rem", "1.5rem")
                is Int -> CountUp(value = value)
                is String -> Text(value)
            }
        }
        HmMuted(label)
    }
}

@Composable
private fun HomeSkeleton() {
    HmDiv(HomeClasses.LIVE) {
        HmDiv(HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_7) {
            SkeletonHead()
            HmBone("62%", "2.5rem")
            HmBone("40%", "1rem")
            HmBone("52%", "1.25rem")
            HmDiv(HomeClasses.DAYS) { repeat(SKELETON_DAYS) { HmBone("2rem", "2rem") } }
            HmBone("7rem", "1.75rem")
        }
        HmDiv(HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_5) {
            SkeletonHead()
            repeat(SKELETON_RUN_ROWS) {
                HmDiv(modifier = Modifier.rowWrap()) {
                    HmBone("4.5rem", "1.5rem")
                    HmBone("55%", "1.25rem")
                }
            }
        }
        HmDiv(HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_4) {
            SkeletonHead()
            HmBone("100%", "6.5rem")
            HmBone("100%", "1rem")
        }
        HmDiv(HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_4) {
            SkeletonHead()
            HmBone("60%", "2.25rem")
            HmBone("45%", "1rem")
            HmBone("7.5rem", "1.75rem")
        }
        HmDiv(HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_4) {
            SkeletonHead()
            HmBone("100%", "3.5rem")
            HmBone("100%", "3.5rem")
        }
    }
}

@Composable
private fun SkeletonHead() {
    HmDiv(modifier = Modifier.rowWrap()) {
        HmBone("2.5rem", "2.5rem")
        HmBone("8rem", "1.25rem")
    }
}

@Composable
private fun SetupSkeleton() {
    HmDiv(HomeClasses.PANEL, HomeClasses.SETUP) {
        HmDiv(HomeClasses.SETUP_SIDE) {
            HmBone("5rem", "1rem")
            HmBone("8.25rem", "8.25rem")
            HmBone("6rem", "1rem")
            HmBone("9rem", "2.5rem")
        }
        HmDiv(HomeClasses.MILESTONES) {
            repeat(SKELETON_MILESTONES) {
                HmDiv(HomeClasses.MILESTONE) {
                    SkeletonHead()
                    HmBone("90%", "1rem")
                    HmBone("75%", "1rem")
                    HmBone("82%", "1rem")
                }
            }
        }
    }
}

private const val SKELETON_MILESTONES = 4
